import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { Vector3 } from 'three';
import Galaxy from './components/Galaxy';
import ControlsBar from './components/ControlsBar';
import SocialButtons from './components/SocialButtons';
import Starfield from './components/Starfield';
import Nebulae from './components/Nebulae';
import ShootingStars from './components/ShootingStars';
import { Project } from './types/Project';
import { projectsData } from './data/projects';
import ProjectDetailPanel from './components/ProjectDetailPanel';
import './App.css';
import './fonts.css';
import { Bloom, EffectComposer } from '@react-three/postprocessing';
import { KernelSize } from 'postprocessing';
import { WarpStreaksOverlay, CameraZoomPulse, InteractionHints } from './components';
import FilterMenu from './components/FilterMenu';
import Sun from './components/Sun';
import Planet from './components/Planet';
import { sunCoreRef } from './scene/sceneRefs';

type FocusTarget = {
  id: string;
  position: Vector3;
  scale: number; // The scale of the object being focused (to adjust distance)
} | null;

type RecenterState = {
  active: boolean;
  needsInit: boolean;
  progress: number;
  startPosition: Vector3;
  startTarget: Vector3;
};

const CameraRig: React.FC<{
  focusRef: React.MutableRefObject<FocusTarget | null>;
  recenterRef: React.MutableRefObject<RecenterState>;
  detailProjectId: string | null;
  defaultDistance: number;
  lockControls?: boolean;
  onRecenterComplete?: () => void;
}> = ({ focusRef, recenterRef, detailProjectId, defaultDistance, lockControls = false, onRecenterComplete }) => {
  const { camera, controls } = useThree((state) => ({
    camera: state.camera,
    controls: state.controls as unknown as { minDistance: number; maxDistance: number; enabled: boolean; target: Vector3; update: () => void } | undefined,
  }));
  const effectiveDistance = defaultDistance || 50;
  const defaultPosition = useRef(
    new Vector3(
      effectiveDistance * 0.55,
      effectiveDistance * 0.42,
      effectiveDistance * 0.85
    )
  );
  const defaultTarget = useRef(new Vector3(0, effectiveDistance * -0.05, 0));
  const desiredPos = useRef(new Vector3());
  const workingDir = useRef(new Vector3());

  useEffect(() => {
    if (!controls) {
      return;
    }
    controls.minDistance = 10;
    controls.maxDistance = 260;
    controls.enabled = !lockControls;
    return () => {
      controls.enabled = true;
    };
  }, [controls, lockControls]);

  useFrame((_, delta) => {
    // We still update controls if locked, but we don't return early
    // so that the camera animation (lerp/recenter) can still happen.
    if (lockControls) {
      controls?.update();
    }
    const target = controls?.target;
    const focus = focusRef.current;

    if (focus) {
      const detailActive = detailProjectId === focus.id;

      const focusLerp = 1 - Math.exp(-delta * (detailActive ? 3.5 : 6));
      if (target) {
        const targetPosition = focus.position.clone();
        if (detailActive) {
          // Planet should be on the left, so we shift the camera target to the right.
          const offset = new Vector3().setFromMatrixColumn(camera.matrixWorld, 0); // Right vector
          const isSun = focus.id === 'sun-profile';
          // Less offset for Sun since we are closer
          offset.multiplyScalar(focus.scale * (isSun ? 0.3 : 1.2));
          targetPosition.add(offset);
        }
        target.lerp(targetPosition, focusLerp);
      }

      workingDir.current.copy(camera.position);
      if (target) {
        workingDir.current.sub(target);
      }
      if (workingDir.current.lengthSq() < 1e-6) {
        workingDir.current.set(0, 0, 1);
      } else {
        workingDir.current.normalize();
      }

      const isSun = focus.id === 'sun-profile';
      // Use 3.0 for planets (far), but 1.3 for Sun (close)
      const distanceMultiplier = detailActive ? (isSun ? 1.3 : 9.0) : 0.85;
      const desiredDistance = Math.max(6, focus.scale * distanceMultiplier);

      desiredPos.current
        .copy(focus.position)
        .addScaledVector(workingDir.current, desiredDistance);

      camera.position.lerp(desiredPos.current, focusLerp);
    } else if (recenterRef.current.active) {
      if (recenterRef.current.needsInit) {
        recenterRef.current.startPosition.copy(camera.position);
        if (target) {
          recenterRef.current.startTarget.copy(target);
        } else {
          recenterRef.current.startTarget.set(0, 0, 0);
        }
        recenterRef.current.progress = 0;
        recenterRef.current.needsInit = false;
      }
      const progress = Math.min(1, recenterRef.current.progress + delta * 0.55);
      recenterRef.current.progress = progress;
      const eased = 1 - Math.pow(1 - progress, 3);
      if (target) {
        target.copy(recenterRef.current.startTarget).lerp(defaultTarget.current, eased);
      }
      camera.position.copy(recenterRef.current.startPosition).lerp(defaultPosition.current, eased);
      if (progress >= 0.999) {
        camera.position.copy(defaultPosition.current);
        if (target) {
          target.copy(defaultTarget.current);
        }
        recenterRef.current.active = false;
        recenterRef.current.progress = 0;
        recenterRef.current.needsInit = true;
        if (onRecenterComplete) onRecenterComplete();
      }
    }

    controls?.update();
  });

  return null;
};

const personalProject: Project = {
  id: 'sun',
  title: 'Devaux Luca',
  description: 'Développeur full-stack passionné par la création d\'expériences web immersives et performantes. J\'aime explorer de nouvelles technologies et repousser les limites du possible. Contactez-moi pour discuter de vos projets !',
  technologies: ['React', 'Node.js', 'TypeScript', 'Three.js', 'Next.js'],
  images: [], // No images for personal project
  githubUrl: 'https://github.com/Luca-Devaux',
  liveUrl: 'https://www.linkedin.com/in/lucadevaux/',
  position: [0, 0, 0],
  color: '#FFFFFF',
  size: 0, // Not relevant for panel
  orbitRadius: 0, // Not relevant for panel
  orbitSpeed: 0, // Not relevant for panel
  categories: ['Web & Mobile'], // Default category for personal project
};

function App() {
  // Always play intro on each page load (remove session persistence)
  const [introDone, setIntroDone] = useState<boolean>(false);
  const [introWarpActive, setIntroWarpActive] = useState<boolean>(true);
  const totalIntroDuration = 1800; // ms fast warp
  const revealStartedRef = useRef<boolean>(false);
  const revealRafRef = useRef<number>(0);
  const [focusedProjectId, setFocusedProjectId] = useState<string | null>(null);
  const [detailProjectId, setDetailProjectId] = useState<string | null>(null);
  const [revealProgress, setRevealProgress] = useState<number>(0); // 0..1: fade-in of solar system
  const focusRef = useRef<FocusTarget | null>(null);
  const recenterRef = useRef<RecenterState>({
    active: false,
    needsInit: false,
    progress: 0,
    startPosition: new Vector3(),
    startTarget: new Vector3(),
  });

  const [isRecentering, setIsRecentering] = useState<boolean>(false);

  const hasFocus = focusedProjectId !== null;

  // Recenter only if no intro warp; otherwise IntroTravel will place us at default framing
  useEffect(() => {
    if (introWarpActive) return;
    recenterRef.current.active = true;
    recenterRef.current.needsInit = true;
    recenterRef.current.progress = 0;
    setIsRecentering(true);
  }, [introWarpActive]);

  // When intro completes, fade in the solar system smoothly
  useEffect(() => {
    if (!introDone) return;
    let raf = 0;
    const start = performance.now();
    const duration = 4500; // ms — slower, more gradual reveal
    const from = 0;
    const to = 1;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // easeInOutSine for gentle, long reveal
      const eased = 0.5 - 0.5 * Math.cos(Math.PI * t);
      setRevealProgress(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [introDone]);

  // No name overlay during warp (keep minimal)

  // Mobile detection for responsive 3D scene
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Compute the system default framing to end the travel exactly there
  // Increase distance on mobile (portrait) to fit the system
  const systemDefaultDistance = useMemo(() => {
    const base = Math.max(...projectsData.map((p) => p.orbitRadius)) * 1.8;
    // Mobile: increase distance to "zoom out" and fit everything in portrait
    return isMobile ? base * 1.05 : base;
  }, [isMobile]);

  const systemEndPosition = useMemo(() => new Vector3(
    systemDefaultDistance * 0.55,
    systemDefaultDistance * 0.42,
    systemDefaultDistance * 0.85
  ), [systemDefaultDistance]);
  const systemEndTarget = useMemo(() => new Vector3(0, systemDefaultDistance * -0.05, 0), [systemDefaultDistance]);

  // Define the "personal" project (Sun)
  const personalProject: Project = useMemo(() => ({
    id: 'sun-profile',
    title: 'Devaux Luca',
    description: "Bonjour ! \n\nJe m'appelle Luca Devaux, développeur en 3ème année à l'IUT de Bordeaux. J'ai 20 ans et je vis à Villenave d'Ornon. Je suis quelqu'un de déterminé qui déteste commencer une tâche sans la finir. J'aime passer du temps sur mes projets et les voir totalement fonctionnels.\n\nJ'ai voulu créer un portfolio différent, une expérience plus ludique qu'une simple présentation de projets. Ce portfolio est inspiré d'un projet de recherche que j'ai effectué au Japon sur l'optimisation de protocoles de communication satellite.\n\nActuellement, je suis à la recherche d'un stage de 12 semaines à partir d'avril 2026. Je ne vise pas forcément un domaine spécifique, ayant touché à beaucoup de technologies durant mes études ! Si mon profil vous intéresse, n'hésitez pas à me contacter par mail ou sur LinkedIn !",
    technologies: ['React', 'TypeScript', 'Three.js', 'Node.js', 'Python', 'C++', 'Java', 'Docker', 'Git', 'Linux'],
    logoUrl: '/logo512.png',
    images: [], // Could add a profile picture here if available
    githubUrl: 'https://github.com/Yusuaa',
    liveUrl: 'https://www.linkedin.com/in/lucadevaux/',
    position: [0, 0, 0],
    color: '#fbbf24',
    size: 10,
    orbitRadius: 0,
    orbitSpeed: 0,
    categories: ['Web & Mobile'], // Default category for personal project
  }), []);

  const handlePlanetFocus = useCallback((project: Project, position: Vector3, scale: number) => {
    if (introWarpActive) return;
    console.log('Focusing planet:', project.title);
    focusRef.current = {
      id: project.id,
      position,
      scale,
    };
    setFocusedProjectId(project.id);
  }, [introWarpActive]);

  const handlePlanetFocusMove = useCallback((project: Project, position: Vector3) => {
    if (focusRef.current && focusRef.current.id === project.id) {
      focusRef.current.position.copy(position);
    }
  }, []);

  const handleProjectDetailsRequest = useCallback((project: Project) => {
    if (introWarpActive) return;
    console.log('Details requested for:', project.title);
    setDetailProjectId(project.id);
    setFocusedProjectId(project.id);
  }, [introWarpActive]);

  const handleProjectDetailClose = useCallback(() => {
    setDetailProjectId(null);
    setFocusedProjectId(null);
    focusRef.current = null;
    recenterRef.current.active = true;
    recenterRef.current.needsInit = true;
    setIsRecentering(true);
  }, []);

  const handleSunClick = useCallback(() => {
    if (introWarpActive) return;
    console.log('Sun clicked');
    // Manually trigger focus for the sun to ensure camera zooms in
    handlePlanetFocus(personalProject, new Vector3(0, 0, 0), 12);
    handleProjectDetailsRequest(personalProject);
  }, [introWarpActive, handleProjectDetailsRequest, handlePlanetFocus, personalProject]);

  const handleUserInteraction = useCallback(() => {
    // If user interacts, we stop auto-recentering if it was active?
    // Actually, OrbitControls handles interaction. We just want to know if we should cancel a programmed move.
    // For now, let's just log or do nothing.
  }, []);

  const handleCanvasMiss = useCallback(() => {
    if (introWarpActive) return;
    // Clicking background clears focus and details
    if (focusedProjectId || detailProjectId) {
      console.log('Canvas miss: clearing focus/details');
      setFocusedProjectId(null);
      setDetailProjectId(null);
      focusRef.current = null;
      recenterRef.current.active = true;
      recenterRef.current.needsInit = true;
      setIsRecentering(true);
    }
  }, [introWarpActive, focusedProjectId, detailProjectId]);

  const activeProject = useMemo(
    () => {
      if (detailProjectId === 'sun-profile') {
        return personalProject;
      }
      return detailProjectId ? projectsData.find((project) => project.id === detailProjectId) ?? null : null
    },
    [detailProjectId, personalProject]
  );

  // Pause state for planetary movement (space key or UI)
  const [paused, setPaused] = useState(false);
  const togglePaused = useCallback(() => {
    setPaused((p) => !p);
  }, []);
  // Speed slider (non-linear mapping to allow very high speeds with fine low-end control)
  const MAX_SPEED = 4; // up to 4x
  // sliderRaw in [0..1]; multiplier = (MAX_SPEED + 1) ** sliderRaw - 1
  const defaultSlider = useMemo(() => Math.log(1 + 1) / Math.log(MAX_SPEED + 1), []); // ~ value for 1x
  const [sliderRaw, setSliderRaw] = useState<number>(defaultSlider);
  const rotationSpeedMultiplier = useMemo(() => Math.pow(MAX_SPEED + 1, sliderRaw) - 1, [sliderRaw]);
  const handleSpeedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderRaw(parseFloat(e.target.value));
  }, []);

  // Category Filtering
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const categories = useMemo(() => {
    const allCategories = projectsData.flatMap(p => p.categories || []);
    // @ts-ignore
    const unique = new Set(allCategories.filter(Boolean));
    return Array.from(unique) as string[];
  }, []);

  const handleIntroComplete = useCallback(() => {
    setIntroWarpActive(false);
    setIntroDone(true);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (!introWarpActive) {
          togglePaused();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePaused, introWarpActive]);

  return (
    <div className="App">
      <Canvas
        dpr={[1, 1.75]}
        camera={{ position: [0, 0, 50], fov: 60 }}
        onPointerDown={handleUserInteraction}
        onWheel={handleUserInteraction}
        onPointerMissed={handleCanvasMiss}
      >
        <ambientLight intensity={0.3} />
        <pointLight position={[0, 0, 0]} intensity={1} />
        <CameraRig
          focusRef={focusRef}
          recenterRef={recenterRef}
          detailProjectId={detailProjectId}
          defaultDistance={systemDefaultDistance}
          lockControls={introWarpActive || isRecentering}
          onRecenterComplete={() => setIsRecentering(false)}
        />
        <Nebulae />
        <Starfield />
        <ShootingStars />
        {introWarpActive && (
          <WarpStreaksOverlay
            duration={totalIntroDuration}
            onComplete={handleIntroComplete}
          />
        )}
        {introDone && (
          <CameraZoomPulse inMs={220} outMs={420} deltaFov={3.5} />
        )}
        <Galaxy
          focusedProjectId={focusedProjectId}
          onPlanetFocus={handlePlanetFocus}
          onPlanetDetails={handleProjectDetailsRequest}
          onPlanetFocusMove={handlePlanetFocusMove}
          onSunClick={handleSunClick}
          activeDetailProjectId={detailProjectId}
          revealProgress={introWarpActive ? 0 : revealProgress}
          paused={paused}
          rotationSpeedMultiplier={rotationSpeedMultiplier}
          activeCategory={activeCategory}
        />
        <OrbitControls
          makeDefault
          enablePan={false}
          enableZoom={false}
          enableRotate
          minDistance={8}
          maxDistance={260}
        />
        <EffectComposer multisampling={2}>
          <Bloom
            mipmapBlur={false}
            intensity={0.85}
            luminanceThreshold={1.2}
            luminanceSmoothing={0.12}
            kernelSize={KernelSize.SMALL}
          />
        </EffectComposer>
      </Canvas>

      {/* Minimalist: no text overlay during warp */}

      {activeProject && (
        <ProjectDetailPanel project={activeProject} onClose={handleProjectDetailClose} />
      )}

      {!activeProject && (
        <FilterMenu
          categories={categories}
          activeCategory={activeCategory}
          onSelectCategory={setActiveCategory}
        />
      )}

      <ControlsBar paused={paused} onTogglePause={togglePaused} />
      {/* Nom minimaliste en haut à gauche */}
      <div className="ui-logo-container">
        <img src="/logo512.png" alt="LucaDev Logo" className="ui-logo" />
      </div>
      {/* Slider vitesse (style volume) */}
      <div className="ui-slider-container">
        <span className="ui-slider-label">VITESSE</span>
        <input
          aria-label="Vitesse rotation"
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={sliderRaw}
          onChange={handleSpeedChange}
          className="ui-slider-input"
        />
        <span className="ui-slider-value">{rotationSpeedMultiplier.toFixed(2)}x</span>
      </div>
      <InteractionHints />
      <SocialButtons githubUrl="https://github.com/Yusuaa" linkedinUrl="https://fr.linkedin.com/in/lucadevaux" size={isMobile ? 42 : 64} />
    </div>
  );
}

export default App;
